<template>
  <div class="filters">
    <ul class="filters__facet-list">
      <FacetDropdown
        v-for="filter in filters"
        :key="filter.name"
        v-bind="{
          name: filter.name,
          data: filter.data,
          icon: filter.icon || '',
          single: filter.single || false,
        }"
        :checkedFilters="currentRefinements[filter.name]"
        @select-filter="handleSelectFilter"
      />
    </ul>
  </div>
</template>

<script>
import CONSTANTS from '@/constants'
import FacetDropdown from '@/components/search/filters/FacetDropdown.vue'

export default {
  name: 'ContentFilters',
  components: {
    FacetDropdown,
  },
  props: {
    filters: {
      type: [Array, Object],
      required: true,
    },
    currentRefinements: {
      type: Object,
      required: true,
    },
    filterMap: {
      type: Object,
      required: true,
    },
    isFreeTrialUser: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      isShowingMore: ['features'],
      hiddenFacets: [],
      mobileFilterOpened: false,
    }
  },
  computed: {
    items() {
      const { data } = this.filter

      return Object.keys(data).map(key => {
        return {
          key,
          count: data[key],
        }
      })
    },
    featureFilters() {
      const featureFilters = []
      const features = this.isFreeTrialUser
        ? this.features
        : this.features.filter(f => f !== 'has_sample_content')
      if (this.filters && this.filters.length) {
        this.filters.map(filter => {
          if (features.includes(filter.name)) {
            if (!filter.data.true) filter.data.true = 0
            featureFilters.push(filter)
          }
        })
      }
      return featureFilters
    },
    nonfeatureFilters() {
      const nonfeatureFilters = []
      if (this.filters && this.filters.length) {
        this.filters.map(filter => {
          if (!this.features.includes(filter.name)) {
            nonfeatureFilters.push(filter)
          }
        })
      }
      return this.fillNonFeatureFilters(nonfeatureFilters)
    },
  },
  methods: {
    fillNonFeatureFilters(filters) {
      return filters.map(filter => {
        if (filter.name !== 'learning_areas.name') return filter
        return this.fillNonFeatureFilter(filter, {
          ...CONSTANTS.LEARNING_AREAS.PRIMARY,
          ...CONSTANTS.LEARNING_AREAS.SECONDARY,
        })
      })
    },
    fillNonFeatureFilter(filter, data) {
      Object.keys(data).map(filterName => {
        if (!Object.prototype.hasOwnProperty.call(filter.data, filterName)) {
          filter.data[filterName] = 0
        }
      })
      return filter
    },
    shouldDisplayShowMore(facet) {
      return !this.isShowingMore.includes(facet.name)
    },
    transformFilterTitle(filter) {
      return this.filterMap[filter] || filter
    },
    transformFilterData(data) {
      return Object.keys(data).map(key => ({
        key,
        count: data[key],
      }))
    },
    updateHiddenFacets(facet) {
      if (this.hiddenFacets.includes(facet)) {
        this.hiddenFacets = this.hiddenFacets.filter(f => f !== facet)
      } else {
        this.hiddenFacets.push(facet)
      }
    },
    handleResults(facet, results) {
      if (!results) return []
      if (this.isShowingMore.includes(facet)) {
        return Object.keys(results)
          .slice(0, 15)
          .reduce((result, key) => {
            result[key] = results[key]

            return result
          }, {})
      }
      return Object.keys(results)
        .slice(0, 5)
        .reduce((result, key) => {
          result[key] = results[key]
          return result
        }, {})
    },
    isButtonShown(results) {
      return Object.keys(results).length > 5
    },
    updateIsShowingMore(facet) {
      if (this.isShowingMore.includes(facet)) {
        this.isShowingMore = this.isShowingMore.filter(f => f !== facet)
      } else {
        this.isShowingMore.push(facet)
      }
    },
    handleSelectFilter({ facet, value }) {
      this.$emit('update-filters', { facet, value })
    },
    filterBubblePressed() {
      window.scrollTo(0, 0)
      this.mobileFilterOpened = !this.mobileFilterOpened
    },
    clearFilters() {
      this.$emit('clear-filter')
    },
  },
}
</script>

<style lang="scss" scoped>
.filters {
  &__facet-list {
    display: flex;
    flex-flow: column;
    gap: 30px;
    width: 100%;
    padding: 0;
    margin: 0;
  }
  &__mobile-top-bar {
    position: fixed;
    height: 40px;
    width: 100%;
    background-color: var(--darker-gray);
    z-index: 1;
    top: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    &-title {
      margin-left: 20px;
      text-transform: capitalize;
      color: var(--white);
      font-size: 20px;
      font-family: var(--font-bold);
    }
    &-back {
      color: var(--white);
      margin-left: 14px;
    }
    &-clear {
      border: none;
      background: none;
      color: var(--white);
      margin-left: 14px;
      font-size: 14px;
      font-family: var(--font-book);
      padding: 0;
      margin-right: 16px;
    }
  }
  &__content {
    margin-top: 55px;
    @include mobile {
      width: 100%;
      margin-top: 0;
    }
  }
  &__hide {
    display: none;
  }
  &__fullscreen {
    position: fixed;
    top: 0;
    left: 0;
    background: var(--dark-brighter);
    z-index: 202;
    width: 100vw;
    height: 100vh;
    overflow: scroll;
    padding: 15px;
  }
  @include mobile {
    overflow: none;
    position: relative;
    display: flex;
    flex-wrap: wrap;
    width: auto;
    height: auto;
    padding: auto;
  }
  &__bubble-buttom-container {
    @include mobile {
      display: none;
    }
  }
  &__activated-dot {
    width: 13px;
    height: 13px;
    background: var(--oranger-gradient);
    position: fixed;
    z-index: 9999;
    bottom: 40px;
    right: 11px;
    border-radius: 10px;
  }
  &__results-button {
    border: none;
    width: 93%;
    height: 40px;
    border-radius: 8px;
    background: var(--white);
    z-index: 203;
    position: fixed;
    bottom: 10px;
    left: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__open-bubble {
    border: none;
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background: var(--white);
    z-index: 203;
    position: fixed;
    bottom: 10px;
    right: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
    &--icon {
      width: 18px;
      height: 17px;
    }
  }

  &__title {
    color: var(--white);
    width: 100%;
    margin: 0 0 30px 0;
    font-size: 1.25rem;
    font-weight: bold;
    text-transform: capitalize;
  }
  &__facet {
    margin: 0 0 10px 0;
    text-transform: uppercase;
    display: flex;
    align-items: center;
    padding: 5px 0;
    width: 100%;
    border-radius: 10px;
    @include tablet {
      padding: 15px 0;
    }
    &--collapsed {
      background: none;
      &:hover {
        background: var(--glass-mid-gray);
        cursor: pointer;
      }
      &:active {
        background: none;
        outline: 1px solid var(--flat-black) !important;
      }
    }
    &--expanded {
      background: var(--glass-mid-gray);
      &:hover {
        background: var(--glass-mid-gray-light);
        cursor: pointer;
      }
      &:active {
        background: none;
        outline: 1px solid var(--flat-black) !important;
      }
    }
  }
  &__facet-title {
    position: relative;
    text-transform: capitalize;
    color: var(--white);
    transition: 0.5s all ease;
    font-family: var(--font-book);
    font-size: 1.1rem;
    margin: 0 0 0 15px;
    width: 100%;
    display: flex;
    align-items: center;
    &:after {
      content: '';
      display: block;
      background: var(--white);
      width: 16px;
      height: 2px;
      border-radius: 2px;
      margin: 0 15px 0 auto;
      transition: 0.5s all ease;
    }
    &--plus {
      position: absolute;
      right: 0;
      &:after {
        content: '';
        display: block;
        background: var(--white);
        width: 16px;
        height: 2px;
        border-radius: 2px;
        margin: 0 15px 0 auto;
        transform: rotate(90deg);
      }
    }
  }
  &__results-wrapper {
    border-radius: 10px;
    margin: 0;
    padding: 0 0 0 5px;
    animation: fadeIn 1s ease-out;
    width: 100%;
    @keyframes fadeIn {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }
  }
  &__result {
    width: 100%;
    border-bottom: 1px solid #323232;
    color: var(--white);
    text-transform: capitalize;
    transition: 0.5s all ease;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 1rem;
    &:hover {
      cursor: pointer;
      color: var(--orange);
    }
    @include tablet {
      padding: 3px;
    }
    &--toggle {
      border: none;
      padding: 0;
    }
  }
  &__item {
    display: flex;
    align-items: center;
    width: 100%;
    padding: 8px 0;
    cursor: pointer;
    &--disabled {
      cursor: initial;
    }
    &-checkbox {
      border: 1px solid var(--mid-gray-dark);
      border-radius: 8px;
      margin: 0 5px;
      appearance: none;
      background: none;
      color: var(--white);
      padding: 11px;
      &:hover:not([disabled]) {
        border: 1px solid var(--white);
        cursor: pointer;
      }
      &:checked {
        border: 1px solid var(--white);
        background: var(--mid-gray-dark-25);
        color: var(--white);
        position: relative;
        display: inline-block;
        @include tablet {
          width: 22px;
          height: 22px;
        }
        &::before {
          transform: rotate(40deg);
          content: '';
          position: absolute;
          width: 1px;
          height: 9px;
          background-color: var(--white);
          left: 12px;
          top: 6px;
        }
        &::after {
          transform: rotate(-45deg);
          content: '';
          position: absolute;
          width: 1px;
          height: 4px;
          background-color: var(--white);
          left: 8px;
          top: 10px;
        }
      }
    }
    &--lab-icon-wrapper {
      width: 24px;
      height: 24px;
      background: var(--purple-gradient);
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 12px;
      margin: 0 0 0 10px;
    }
    &-title {
      font-size: 1rem;
      margin: 0 0 0 10px;
      color: var(--white);
      padding: 0;
      &--disabled {
        color: gray;
      }
    }
    &-count {
      font-size: 1rem;
      margin: 0 0 0 auto;
      border-radius: 25px;
      color: var(--white);
      padding: 0;
      &--disabled {
        color: gray;
      }
    }
  }
  &__show-more-btn {
    &__wrapper {
      margin: 15px auto 30px auto;
      width: auto;
      display: flex;
      justify-content: center;
    }
    display: flex;
    align-items: center;
    color: var(--white);
    border: none;
    background: var(--menu-btn);
    border-radius: 25px;
    cursor: pointer;
    justify-content: center;
    text-transform: capitalize;
    font-size: 0.8rem;
    padding: 5px 7px 5px 15px;
    &:hover {
      border: 1px solid var(--dove-gray);
      padding: 4px 6px 4px 14px;
      background: var(--emperor-gray);
      box-shadow: var(--white-medium-box-shadow);
    }
    &:active {
      background: var(--darker-gray);
    }
    &__arrow {
      margin: 0 5px 0 10px;
    }
  }
}
</style>
